/**
 * @file LegalPage.jsx
 * @description Standalone public legal document pages. Rendered at
 *   /terms-of-use and /privacy-policy (old /terms and /privacy slugs 301
 *   via PAGE_ALIASES in App.js) and accessible to all roles including
 *   guests — including as direct links from Stripe's business-info fields.
 * @author Victor Carpinelli
 * @version 1.1.0
 * @module components/LegalPage
 */
// Has its own minimal header so TopNav is suppressed for these views.

import { useTranslation } from "react-i18next";
import { T } from "../theme";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePageSeo } from "../lib/seo";
import Logo from "./Logo";
import {
  TOS_VERSION, PP_VERSION, DRP_VERSION, LEGAL_EFFECTIVE_DATE,
  TOS_SECTIONS, PP_SECTIONS, DRP_SECTIONS,
} from "../lib/legalText";

export default function LegalPage({ type, onBack }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  // type ∈ "tos" | "pp" | "drp"
  const title =
    type === "tos" ? t("legal.termsOfService") :
    type === "drp" ? t("legal.dataRetentionPolicy") :
                     t("legal.privacyPolicy");
  const canonical =
    type === "tos" ? "/terms-of-use" :
    type === "drp" ? "/retention" :
                     "/privacy-policy";

  // Utility/legal pages — not part of the SEO surface, but still get a real
  // <title> since they're linked directly from Stripe checkout/portal.
  usePageSeo({ title: `${title} | ABAcases`, canonical, noindex: true });
  const version =
    type === "tos" ? TOS_VERSION :
    type === "drp" ? DRP_VERSION :
                     PP_VERSION;
  const sections =
    type === "tos" ? TOS_SECTIONS :
    type === "drp" ? DRP_SECTIONS :
                     PP_SECTIONS;

  return (
    <div style={{
      minHeight: "100vh",
      background: T.bg,
      fontFamily: "'Inter', 'DM Sans', sans-serif",
      display: "flex",
      flexDirection: "column",
      overflowX: "hidden",
    }}>

      {/* Minimal header */}
      <div style={{
        background: T.surface,
        borderBottom: `1px solid ${T.border}`,
        padding: isMobile ? "12px 16px" : "14px 32px",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? 10 : 0,
        justifyContent: "space-between",
        flexShrink: 0,
        position: "sticky",
        top: 0,
        zIndex: 100,
        boxShadow: "0 1px 0 rgba(18,16,58,0.05)",
      }}>
        {/* Top row: back button + logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button
            onClick={() => onBack && onBack()}
            style={{
              display: "flex", alignItems: "center", gap: 5,
              background: "transparent", border: `1px solid ${T.border}`,
              borderRadius: 7, padding: "5px 10px",
              fontSize: 12, fontWeight: 500, color: T.textMid,
              cursor: "pointer", fontFamily: "'Inter', sans-serif",
            }}
          >
            ← {t("common.back")}
          </button>
          <Logo variant="lockup" height={26} alt="ABAcases" />
        </div>

        {/* Tab switcher — full width on mobile */}
        <div style={{
          display: "flex", gap: 2,
          background: T.surfaceAlt,
          borderRadius: 8, padding: 3,
          border: `1px solid ${T.border}`,
          width: isMobile ? "100%" : "auto",
        }}>
          {[["tos", t("legal.termsOfService")], ["pp", t("legal.privacyPolicy")]].map(([tabType, label]) => {
            const isActive = tabType === type;
            return (
              <button
                key={tabType}
                onClick={() => !isActive && onBack && onBack(tabType === "tos" ? "terms-of-use" : "privacy-policy")}
                style={{
                  flex: isMobile ? 1 : undefined,
                  padding: isMobile ? "9px 0" : "6px 16px",
                  borderRadius: 6, fontSize: 12,
                  fontWeight: isActive ? 700 : 500,
                  fontFamily: "'Inter', sans-serif",
                  border: "none", cursor: "pointer",
                  background: isActive ? T.surface : "transparent",
                  color: isActive ? T.text : T.textLight,
                  boxShadow: isActive ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                  transition: "all 0.15s",
                }}
              >{label}</button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div style={{
        flex: 1,
        minHeight: 0,
        maxWidth: 840,
        width: "100%",
        margin: "0 auto",
        padding: isMobile ? "20px 14px 48px" : "40px 24px 72px",
        boxSizing: "border-box",
        overflowWrap: "anywhere",
      }}>
        <div style={{
          background: "#FCFDFE",
          border: `1px solid ${T.border}`,
          borderRadius: isMobile ? 18 : 24,
          boxShadow: isMobile
            ? "0 10px 28px rgba(18,16,58,0.08), 0 2px 8px rgba(18,16,58,0.05)"
            : "0 18px 48px rgba(18,16,58,0.10), 0 6px 18px rgba(18,16,58,0.06)",
          padding: isMobile ? "20px 18px 24px" : "32px 44px 36px",
          position: "relative",
        }}>

          {/* Title block */}
          <h1 style={{
            fontSize: isMobile ? 24 : 32, fontWeight: 900, color: T.text,
            letterSpacing: "-0.03em", margin: "0 0 6px",
            lineHeight: 1.15,
          }}>
            {title}
          </h1>
          <div style={{
            fontSize: 12, color: T.textLight, marginBottom: isMobile ? 28 : 40,
            display: "flex", flexWrap: "wrap", gap: isMobile ? 8 : 16, alignItems: "center",
          }}>
            <span>Version {version}</span>
            <span style={{ color: T.border }}>·</span>
            <span>Effective {LEGAL_EFFECTIVE_DATE}</span>
            {!isMobile && <><span style={{ color: T.border }}>·</span><span>abacases.com</span></>}
          </div>

          {/* Sections */}
          {sections.map((section, idx) => (
            <div
              key={section.id}
              style={{
                marginBottom: 36,
                paddingBottom: 36,
                borderBottom: idx < sections.length - 1 ? `1px solid ${T.border}` : "none",
              }}
            >
              {section.heading && (
                <h2 style={{
                  fontSize: 15, fontWeight: 800, color: T.text,
                  letterSpacing: "-0.015em",
                  margin: "0 0 12px",
                  lineHeight: 1.3,
                }}>
                  {section.heading}
                </h2>
              )}
              <div style={{
                fontSize: 14, color: T.textMid,
                whiteSpace: "pre-line", lineHeight: 1.8,
              }}>
                {section.body}
              </div>
            </div>
          ))}

          {/* Footer */}
          <div style={{
            padding: "20px 24px",
            background: T.surfaceAlt,
            borderRadius: 12,
            border: `1px solid ${T.border}`,
            fontSize: 12, color: T.textLight, lineHeight: 1.65,
            marginTop: 8,
          }}>
            <em>{t('legal.footerDisclaimer')}</em>
            <div style={{ marginTop: 12, fontWeight: 600, color: T.textMid }}>
              ABAcases · abacases.com ·{" "}
              <a href="mailto:support@abacases.com" style={{ color: T.blue, textDecoration: "none" }}>
                support@abacases.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
